import type { CSSProperties } from 'react'
import { cx } from '../../cx'
import { LOGO_CHEVRON_PATH } from '../Logo/Logo'
import styles from './Avatar.module.css'

export interface AvatarProps {
  /** Imię i nazwisko → inicjały; brak → fallback z chevronem wg księgi. */
  name?: string
  /**
   * `organization` — nazwa firmy: forma prawna („Sp. z o.o.”, „S.A.”) nie daje
   * inicjału, więc „Astra HR Sp. z o.o.” to „AH”, a nie „AO” (fala 9, grupa 148).
   */
  kind?: 'person' | 'organization'
  size?: number
  className?: string
}

/** Forma prawna na końcu nazwy firmy — odcinana przed liczeniem inicjałów. */
const LEGAL_FORM_RE =
  /\s+(?:sp\.\s*z\s*o\.\s*o\.|spółka\s+z\s+ograniczoną\s+odpowiedzialnością|s\.\s*a\.|spółka\s+akcyjna|sp\.\s*j\.|sp\.\s*k\.|sp\.\s*p\.|s\.\s*k\.\s*a\.|s\.\s*c\.|spółka\s+\S+)\s*$/iu

function organizationName(name: string): string {
  const stripped = name.replace(LEGAL_FORM_RE, '').trim()
  return stripped === '' ? name : stripped
}

function initialsOf(name: string): string {
  /* Tokeny bez litery („&”, „—”) nie dają inicjału. */
  const parts = name.split(/\s+/).filter((part) => /\p{L}/u.test(part))
  const first = parts[0]?.charAt(0) ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : ''
  return (first + last).toLocaleUpperCase('pl-PL')
}

export function Avatar({ name, kind = 'person', size = 40, className }: AvatarProps) {
  const trimmed = name?.trim() ?? ''
  const style: CSSProperties = {
    width: size,
    height: size,
    fontSize: Math.round(size * 0.38),
  }

  if (trimmed === '') {
    /* Fallback wg księgi (sekcja 07): okrąg --k1-pink-soft, border --k1-border,
       chevron --k1-pink-dark */
    const chevronSize = Math.round(size * 0.46)
    return (
      <span className={cx(styles.avatar, className)} style={style} aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          width={chevronSize}
          height={chevronSize}
          fill="none"
          stroke="currentColor"
          strokeWidth={2.8}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d={LOGO_CHEVRON_PATH} />
        </svg>
      </span>
    )
  }

  return (
    <span role="img" aria-label={trimmed} className={cx(styles.avatar, className)} style={style}>
      {initialsOf(kind === 'organization' ? organizationName(trimmed) : trimmed)}
    </span>
  )
}
